/**
 * Sonda de RUM del panel — ADR-0012, paso 5.
 *
 * Mide lo único que el equipo siente todo el día: **cuánto tarda una navegación suave en mostrar
 * contenido**. No renderiza nada.
 *
 * ⭐ EL TRUCO: QUÉ CUENTA COMO "YA SE VE"
 * Medir hasta que el componente monta sería mentir — monta vacío y los datos llegan después; ése es
 * justamente el waterfall que el ADR describe. Y pedirle a cada una de las 239 páginas que avise
 * cuando terminó sería una instrumentación que nadie mantiene.
 *
 * La caché da la señal gratis: `useIsFetching()` es la cantidad de consultas en vuelo. Una
 * navegación terminó cuando **pintó al menos un cuadro y no queda ninguna consulta corriendo**. Eso
 * es "la lista apareció", sin tocar ninguna pantalla.
 *
 * De ahí salen los dos casos, y la diferencia entre ellos es el marcador del paso 3:
 *   · Arrancó alguna consulta → se mide hasta que terminan todas. `cache_hit = false`.
 *   · No arrancó ninguna     → se sirvió de memoria. Se mide hasta el primer cuadro pintado y
 *                              `cache_hit = true`. Debería ser el caso frecuente al volver a una
 *                              pantalla ya visitada.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsFetching } from '@tanstack/react-query';
import { useAuth, useFeatureFlags } from '@smarty/shared';
import { encolarRum, iniciarEnvioRum, normalizarRuta } from '@/lib/analytics/panelRum';
import { marcarHito, hitosPendientes, alMarcarHito } from '@/lib/analytics/hitosDelArranque';

/**
 * Cuánto se espera a ver si la navegación dispara alguna consulta antes de declararla servida de
 * memoria. Tiene que ser mayor que el retraso entre el cambio de ruta y el `useEffect` de la página
 * que arranca la consulta (un par de cuadros), y bastante menor que cualquier navegación que se
 * sienta lenta, para no inflar el número de las rápidas.
 *
 * 🪤 El valor reportado NO es este plazo: es el momento en que se pintó. La espera sólo sirve para
 * decidir de qué caso se trata; si se reportara el plazo, toda navegación cacheada mediría 250 ms
 * y la métrica diría que la caché no sirvió.
 */
const GRACIA_MS = 250;

/** Más que esto no es una navegación: es una pestaña que estuvo dormida o una consulta colgada. */
const TOPE_MS = 30_000;

type Fase = 'inactivo' | 'esperando' | 'buscando';

export function PanelRumProbe(): null {
  const { pathname } = useLocation();
  const enVuelo = useIsFetching();
  const { tenantId, user } = useAuth();
  /*
   * 🪤 LA PUERTA DE PERMISOS SE MIDE DESDE ACA porque `FeatureFlagsContext` vive en el paquete
   *    compartido y no puede importar la instrumentacion de `src`. Esta sonda ya esta DENTRO del
   *    proveedor (ver `CapaRaiz`), asi que ve exactamente el mismo momento en que la puerta abre
   *    — sin que un paquete generico tenga que saber del RUM del panel.
   */
  const { loading: permisosCargando } = useFeatureFlags();

  const fase = useRef<Fase>('inactivo');
  const t0 = useRef(0);
  const pintadoEn = useRef<number | null>(null);
  const ruta = useRef('');
  const graciaId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rutaPrevia = useRef<string | null>(null);
  const identidad = useRef<{ tenantId?: string; userId?: string }>({});

  // 🪤 En un efecto, no en el cuerpo del render: escribir un ref durante el render es un efecto
  // secundario en medio del render y React puede descartar ese trabajo en modo concurrente. Los
  // callbacks que leen esto (web-vitals, el reporte) viven fuera del ciclo y necesitan el valor
  // fresco, que es para lo que está el ref.
  useEffect(() => {
    identidad.current = { tenantId: tenantId ?? undefined, userId: user?.id };
  }, [tenantId, user?.id]);

  useEffect(() => iniciarEnvioRum(), []);

  /*
   * La puerta de permisos abrio: hasta este momento `FeatureProtectedRoute` mostraba un spinner y
   * la pantalla protegida ni se renderizaba.
   *
   * 🚨 SE MIDE PORQUE UNA PREDICCION MIA FALLO. Dije que recordar los permisos borraria el tramo
   *    entre «llegaron los datos» y «monto la pantalla»; bajo de 451 a ~343 ms y no mas. La puerta
   *    era PARTE del problema, no todo — y sin medirla no se puede saber cuanta parte.
   */
  useEffect(() => {
    if (!permisosCargando) marcarHito('permisos');
  }, [permisosCargando]);

  /*
   * Los HITOS del arranque. Ver `hitosDelArranque`: se anotan sincronicamente desde donde ocurren
   * y se mandan aca, que es el unico lugar que conoce la identidad del usuario.
   *
   * 🪤 El hito de la sesion se marca ACA y no dentro de AuthContext: el contexto vive en
   *    `packages/shared`, que no puede importar de `src`. El momento es el mismo — cuando el panel
   *    ya sabe de que negocio pedir — y asi la instrumentacion no se filtra al paquete compartido.
   */
  useEffect(() => {
    if (!tenantId || !user?.id) return;
    const ruta = normalizarRuta(window.location.pathname);
    const vaciar = () => {
      for (const h of hitosPendientes()) {
        encolarRum({
          tenant_id: tenantId, user_id: user.id, route: ruta,
          metric: `hito_${h.nombre}`, value_ms: h.ms, cache_hit: null,
        });
      }
    };
    // 🪤 Se queda ESCUCHANDO. Mandar una sola vez perdia `filas`, que ocurre despues de que la
    //    sesion resuelve: el instrumento media medio arranque y parecia completo.
    alMarcarHito(vaciar);
    marcarHito('sesion');
    vaciar();
  }, [tenantId, user?.id]);

  // Arranque duro: lo que tardó el navegador desde que pidió el documento hasta que la app quedó
  // interactiva. Es el número que mueven los pasos 4 y 2 (bundle y router), y sin él una mejora del
  // arranque no se distingue de una mejora de la navegación.
  useEffect(() => {
    if (!tenantId || !user?.id) return;
    let cancelado = false;
    const t = setTimeout(() => {
      try {
        const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (nav && nav.duration > 0) {
          encolarRum({
            tenant_id: tenantId, user_id: user.id, route: normalizarRuta(window.location.pathname),
            metric: 'nav_hard', value_ms: nav.duration, cache_hit: null,
          });
        }
      } catch { /* la telemetría no rompe nada */ }

      // La librería entra por `import()` para no volver al arranque: sacar peso del entry fue el
      // paso 4 y sería absurdo que la sonda que lo mide lo engorde.
      void import('web-vitals').then(({ onLCP, onFCP, onTTFB, onINP }) => {
        if (cancelado) return;
        const reportar = (metric: 'LCP' | 'FCP' | 'TTFB' | 'INP') => (m: { value: number }) => {
          const id = identidad.current;
          if (!id.tenantId || !id.userId) return;
          encolarRum({
            tenant_id: id.tenantId, user_id: id.userId, route: normalizarRuta(window.location.pathname),
            metric, value_ms: m.value, cache_hit: null,
          });
        };
        onLCP(reportar('LCP')); onFCP(reportar('FCP')); onTTFB(reportar('TTFB')); onINP(reportar('INP'));
      }).catch(() => { /* sin web-vitals seguimos midiendo navegación, que es lo importante */ });
    }, 0);
    return () => { cancelado = true; clearTimeout(t); };
  }, [tenantId, user?.id]);

  const reportar = (ms: number, cacheHit: boolean) => {
    const id = identidad.current;
    fase.current = 'inactivo';
    if (graciaId.current) { clearTimeout(graciaId.current); graciaId.current = null; }
    if (!id.tenantId || !id.userId) return;
    // 🪤 Con la pestaña oculta el navegador estrangula los temporizadores y `requestAnimationFrame`
    // directamente no corre: cualquier número medido ahí es del throttling, no de la app. Se
    // descarta en vez de guardar basura que después ensucia el p75.
    if (document.visibilityState === 'hidden') return;
    if (ms > TOPE_MS) return;
    encolarRum({
      tenant_id: id.tenantId, user_id: id.userId, route: ruta.current,
      metric: 'nav_soft', value_ms: ms, cache_hit: cacheHit,
    });
  };

  // Cambio de ruta: acá empieza a correr el cronómetro.
  useEffect(() => {
    const previa = rutaPrevia.current;
    rutaPrevia.current = pathname;
    // La primera vuelta es el arranque duro, no una navegación suave: ya lo mide el efecto de
    // arriba, y contarlo también acá metería el tiempo de carga completo en el p75 de la navegación.
    if (previa === null || previa === pathname) return;

    t0.current = performance.now();
    pintadoEn.current = null;
    ruta.current = normalizarRuta(pathname);
    fase.current = 'esperando';

    // Dos cuadros: el primero es el que React usa para montar; el segundo ya tiene pixeles.
    let raf2 = 0;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => { pintadoEn.current = performance.now(); });
    });

    if (graciaId.current) clearTimeout(graciaId.current);
    graciaId.current = setTimeout(() => {
      // Nadie pidió nada en el plazo: la pantalla se sirvió de memoria.
      if (fase.current === 'esperando') {
        reportar((pintadoEn.current ?? performance.now()) - t0.current, true);
      }
    }, GRACIA_MS);

    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
      // Navegar de nuevo antes de terminar ABANDONA la medición anterior. Reportarla mediría dos
      // navegaciones encimadas como una sola y larga.
      if (graciaId.current) { clearTimeout(graciaId.current); graciaId.current = null; }
      fase.current = 'inactivo';
    };
    // `reportar` se redefine en cada render pero sólo lee refs; incluirlo reiniciaría el cronómetro
    // en cada render, que es justo lo que no se quiere.
     
  }, [pathname]);

  // Transiciones de "hay consultas en vuelo".
  useEffect(() => {
    if (fase.current === 'esperando' && enVuelo > 0) {
      fase.current = 'buscando';
      if (graciaId.current) { clearTimeout(graciaId.current); graciaId.current = null; }
      return;
    }
    if (fase.current === 'buscando' && enVuelo === 0) {
      // Un cuadro más: cuando la última consulta termina, sus datos todavía no están pintados.
      const raf = requestAnimationFrame(() => reportar(performance.now() - t0.current, false));
      return () => cancelAnimationFrame(raf);
    }
     
  }, [enVuelo]);

  return null;
}
